import { Button, Stack } from '@mui/material';
import { DataGridPro, gridClasses, useGridApiRef } from '@mui/x-data-grid-pro';
import { Tool } from '@procureai/agents';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { ChatLoadingIndicator } from '../ChatMessage/indicators/ChatLoadingIndicator';
import { ChatToolResultIndicator } from '../ChatMessage/indicators/ChatToolResultIndicator';
import { SvbrgSearchPurchaseOrderItemInput, SvbrgSearchPurchaseOrderItemOutput } from '@/generated/gql/graphql';
import { I18nNamespace } from '@/legacy/common/types';
import { RouteId } from '~/routes';
import { usePurchaseOrderItemResultDataGrid } from '~/routes/IntakeChat/components/PurchaseOrderItemSearchResult/PurchaseOrderItemSearchResult.dataGrid';
import { useSearchURL } from '~/routes/Search/hooks/useSearchURL/useSearchURL';

import { ToolResultComponent } from '.';
import { isWildcardOrEmptyQuery } from './toolLabel.utils';

export const PurchaseOrderItemTool: ToolResultComponent<
  Tool.searchPurchaseOrderItem,
  SvbrgSearchPurchaseOrderItemInput,
  SvbrgSearchPurchaseOrderItemOutput
> = ({ tool, toolName }) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: `Chat.tools.${toolName}` });

  const config = usePurchaseOrderItemResultDataGrid();
  const apiRef = useGridApiRef();

  const { searchParams, hasSearch, domain } = useSearchURL({
    query: tool.input?.query,
  });

  const query = tool.input?.query;
  const isNoQuery = isWildcardOrEmptyQuery(query);
  const callMessage = isNoQuery ? t('callNoQuery') : t('call', { query });
  const resultMessage = isNoQuery ? t('resultNoQuery') : t('result', { query });

  switch (tool.state) {
    case 'input-available':
    case 'input-streaming':
      return <ChatLoadingIndicator message={callMessage} />;

    case 'output-available': {
      const items = tool.output.items ?? [];

      // Zero-result case: render a non-interactive indicator. Expanding into an empty
      // DataGrid was the UX regression reported in BUG-2062 (NE UAT) — the agent's
      // preamble already describes the absence in text when items.length === 0.
      if (items.length === 0) {
        return <ChatToolResultIndicator message={resultMessage} defaultOpen={false} disabled={true} />;
      }

      return (
        <ChatToolResultIndicator message={resultMessage} defaultOpen={false} disabled={false}>
          <DataGridPro
            {...config}
            sx={{
              [`&.${gridClasses.root}`]: {
                '--DataGrid-containerBackground': 'transparent',
              },
            }}
            apiRef={apiRef}
            disableColumnMenu
            rows={items}
            getRowId={(row) => row.id}
            slots={{
              footer: () => (
                <Stack direction="row">
                  {hasSearch && (
                    <Button
                      component={RouterLink}
                      to={{
                        pathname: RouteId.searchDetail(domain),
                        search: searchParams,
                      }}
                      sx={{ m: 4 }}
                    >
                      {t('footer.button')}
                    </Button>
                  )}
                </Stack>
              ),
            }}
          />
        </ChatToolResultIndicator>
      );
    }
  }
};
